import React, { useEffect, useState, useRef } from 'react';
import {
  Card, Table, Button, Tag, Typography, Space, Alert, Empty,
  Popconfirm, Modal, Form, Input, Select, message,
} from 'antd';
import {
  PlusOutlined, DeleteOutlined, StopOutlined, SearchOutlined,
} from '@ant-design/icons';
import axios from 'axios';

const { Text } = Typography;
const { Option } = Select;

const TYPE_STYLE = {
  bounce:      { color: 'var(--danger)',  bg: 'var(--danger-bg)',  border: 'var(--danger)'  },
  complaint:   { color: 'var(--warn)',    bg: 'var(--warn-bg)',    border: 'var(--warn)'    },
  unsubscribe: { color: 'var(--text-2)',  bg: 'var(--surface-2)', border: 'var(--border)'  },
};

const Suppression = () => {
  const [suppressions, setSuppressions] = useState([]);
  const [total,        setTotal]        = useState(0);
  const [currentPage,  setCurrentPage]  = useState(1);
  const [pageSize]                      = useState(20);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const searchDebounceRef = useRef(null);
  const [addModal, setAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchSuppressions = async (page = currentPage, searchVal = search) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get('/api/suppression', {
        headers,
        params: { page, limit: pageSize, search: searchVal },
      });
      const list = res.data.data ?? res.data;
      setSuppressions(list);
      setTotal(res.data.total ?? list.length);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load suppression list');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSuppressions(1, ''); }, []); // eslint-disable-line

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    setCurrentPage(1);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => fetchSuppressions(1, val), 350);
  };

  const handleAdd = async (values) => {
    setSaving(true);
    try {
      await axios.post('/api/suppression', values, { headers });
      form.resetFields();
      setAddModal(false);
      message.success('Email added to suppression list');
      fetchSuppressions(currentPage, search);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add suppression');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`/api/suppression/${id}`, { headers });
      message.success('Removed from suppression list');
      const newPage = suppressions.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      setCurrentPage(newPage);
      fetchSuppressions(newPage, search);
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const columns = [
    {
      title: 'Email', dataIndex: 'email', width: 220, ellipsis: true,
      render: email => <Text strong style={{ fontFamily: 'var(--font-mono)', fontSize: 13, wordBreak: 'break-word' }}>{email}</Text>,
    },
    {
      title: 'Type', dataIndex: 'type', width: 130,
      render: t => {
        const s = TYPE_STYLE[t] || TYPE_STYLE.unsubscribe;
        return (
          <span style={{
            fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 20,
            background: s.bg, color: s.color, border: `1px solid ${s.border}`,
            textTransform: 'capitalize', display: 'inline-block',
          }}>
            {t}
          </span>
        );
      },
    },
    { title: 'Reason', dataIndex: 'reason', render: r => r || '—' },
    {
      title: 'Added', dataIndex: 'created_at', width: 160,
      render: d => <Text style={{ fontSize: 12, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{new Date(d).toLocaleString()}</Text>,
    },
    {
      title: '', align: 'right', width: 80,
      render: (_, record) => (
        <Popconfirm
          title="Remove from suppression list?"
          description="This email will be eligible to receive future campaigns."
          onConfirm={() => handleDelete(record.id)}
          okText="Remove"
          okButtonProps={{ danger: true }}
        >
          <Button danger icon={<DeleteOutlined />} size="small">Remove</Button>
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <Text style={{ color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>
          <strong style={{ color: 'var(--text)', fontFamily: 'var(--font-mono)' }}>{total}</strong> suppressed email{total !== 1 ? 's' : ''} — these addresses won't receive campaigns
        </Text>
        <Button type="primary" icon={<PlusOutlined />} size="large" onClick={() => setAddModal(true)} style={{ borderRadius: 'var(--r-md)', fontWeight: 600 }}>
          Add Email
        </Button>
      </div>

      {error && <Alert message={error} type="error" showIcon closable onClose={() => setError('')} style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }} />}

      <Card style={{ borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}>
        <div style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search by email or reason…"
            prefix={<SearchOutlined style={{ color: 'var(--text-3)' }} />}
            value={search}
            onChange={handleSearchChange}
            onClear={() => { setSearch(''); setCurrentPage(1); fetchSuppressions(1, ''); }}
            style={{ maxWidth: 320, borderRadius: 'var(--r-md)' }}
            allowClear
          />
        </div>
        <Table
          dataSource={suppressions}
          columns={columns}
          rowKey="id"
          loading={loading}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No suppressed emails — great deliverability!" /> }}
          pagination={{
            current: currentPage,
            pageSize,
            total,
            showTotal: t => `${t} record${t !== 1 ? 's' : ''}`,
            onChange: (page) => { setCurrentPage(page); fetchSuppressions(page, search); },
            showSizeChanger: false,
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>

      <Modal
        title={
          <Space>
            <div style={{ width: 32, height: 32, background: 'var(--danger-bg)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <StopOutlined style={{ color: 'var(--danger)' }} />
            </div>
            <span>Add to Suppression List</span>
          </Space>
        }
        open={addModal}
        onCancel={() => { setAddModal(false); form.resetFields(); }}
        footer={null}
        destroyOnHidden
        width={440}
      >
        <Form form={form} onFinish={handleAdd} layout="vertical" style={{ marginTop: 20 }}>
          <Form.Item name="email" label="Email Address" rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}>
            <Input placeholder="email@example.com" size="large" style={{ borderRadius: 'var(--r-md)' }} />
          </Form.Item>
          <Form.Item name="type" label="Suppression Type" rules={[{ required: true, message: 'Select a type' }]}>
            <Select placeholder="Select type" size="large">
              <Option value="unsubscribe">Unsubscribe</Option>
              <Option value="bounce">Bounce</Option>
              <Option value="complaint">Complaint</Option>
            </Select>
          </Form.Item>
          <Form.Item name="reason" label="Reason (optional)">
            <Input placeholder="e.g., User requested removal" style={{ borderRadius: 'var(--r-md)' }} />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => { setAddModal(false); form.resetFields(); }}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saving} danger style={{ borderRadius: 8 }}>
                Add to Suppression
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Suppression;
